import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { imgSrc } from "@/lib/api";

function MaskedTitle({ text, k }) {
  const words = (text || "").split(" ");
  return (
    <h1 key={k} className="text-4xl sm:text-5xl lg:text-7xl font-serif font-bold tracking-tight text-cream leading-[1.05]" data-testid="hero-title">
      {words.map((w, i) => (
        <span key={i} className="inline-block overflow-hidden pb-1 -mb-1 align-bottom">
          <motion.span
            className="inline-block"
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 + i * 0.06, ease: [0.22, 1, 0.36, 1] }}
          >
            {w}&nbsp;
          </motion.span>
        </span>
      ))}
    </h1>
  );
}

export default function HeroSlider({ slides = [], base = "" }) {
  const [idx, setIdx] = useState(0);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const slide = slides[idx] || {};
  const count = slides.length;

  useEffect(() => {
    if (count < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % count), 6500);
    return () => clearInterval(t);
  }, [count]);

  const go = (dir) => setIdx((i) => (i + dir + count) % count);

  return (
    <section ref={ref} className="relative mx-6 sm:mx-16 lg:mx-24 mt-4 mb-4 h-[74vh] min-h-[480px] overflow-hidden rounded-md border border-ink/10 shadow-xl bg-cast" data-testid="hero-slider">
      <AnimatePresence mode="popLayout">
        <motion.div
          key={idx}
          className="absolute inset-0"
          initial={{ opacity: 0, scale: 1.06 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
        >
          <motion.div style={{ y }} className="absolute inset-0">
            {slide.image && (
              <img src={imgSrc(slide.image)} alt={slide.title || "Chef Blaine Guillot catering"} className="h-full w-full object-cover" />
            )}
          </motion.div>
          <div className="absolute inset-0 bg-gradient-to-t from-cast via-cast/55 to-cast/25" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(14,12,10,0.55)_100%)]" />
        </motion.div>
      </AnimatePresence>

      <div className="relative z-10 flex h-full flex-col justify-end pb-24 sm:pb-28 px-6 sm:px-12 lg:px-20 max-w-7xl mx-auto">
        <motion.p
          key={`e-${idx}`}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="text-xs uppercase font-mono tracking-[0.3em] text-brass mb-5"
          data-testid="hero-eyebrow"
        >
          Chef Blaine Guillot — South Louisiana
        </motion.p>
        <MaskedTitle text={slide.title} k={idx} />
        {slide.subtitle && (
          <motion.p
            key={`s-${idx}`}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 0.7 }}
            className="mt-5 max-w-xl text-base sm:text-lg text-sand/80 leading-relaxed"
            data-testid="hero-subtitle"
          >
            {slide.subtitle}
          </motion.p>
        )}
        {slide.cta_label && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.7 }} className="mt-8">
            <Link
              to={slide.cta_link?.startsWith("#") ? "#" : `${base}${slide.cta_link || "/contact"}`}
              data-testid="hero-cta-button"
              className="inline-flex items-center gap-3 rounded-full bg-brass px-8 py-4 text-sm font-semibold uppercase tracking-widest text-cast transition-colors duration-300 hover:bg-flame hover:text-cream"
            >
              {slide.cta_label}
              <span className="h-2 w-2 rounded-full bg-current" />
            </Link>
          </motion.div>
        )}
      </div>

      {count > 1 && (
        <div className="absolute bottom-8 right-6 sm:right-12 z-20 flex items-center gap-3">
          <button onClick={() => go(-1)} data-testid="hero-prev-button" aria-label="Previous slide" className="rounded-full border border-brass/30 p-2.5 text-brass transition-colors hover:bg-brass hover:text-cast">
            <ChevronLeft size={18} />
          </button>
          <div className="flex gap-2">
            {slides.map((_, i) => (
              <button key={i} onClick={() => setIdx(i)} data-testid={`hero-dot-${i}`} aria-label={`Slide ${i + 1}`} className={`h-1.5 rounded-full transition-all duration-500 ${i === idx ? "w-8 bg-brass" : "w-1.5 bg-cream/30"}`} />
            ))}
          </div>
          <button onClick={() => go(1)} data-testid="hero-next-button" aria-label="Next slide" className="rounded-full border border-brass/30 p-2.5 text-brass transition-colors hover:bg-brass hover:text-cast">
            <ChevronRight size={18} />
          </button>
        </div>
      )}
    </section>
  );
}
